'use client';

import { ChangeEvent } from 'react';
import { SupportData } from './DiagnosticFormInteractive';

interface StepSupportPreferencesProps {
  data: SupportData;
  onChange: (data: SupportData) => void;
}

const SUPPORT_OPTIONS = [
  {
    id: 'monthly_retainer',
    label: 'Apoio Mensal Contínuo',
    description: 'Acompanhamento regular com horas mensais dedicadas à sua Junta de Freguesia',
    badge: 'Mais solicitado',
  },
  {
    id: 'punctual_support',
    label: 'Apoio Pontual',
    description: 'Intervenção específica para resolver situações concretas quando necessário',
    badge: null,
  },
  {
    id: 'initial_diagnosis',
    label: 'Diagnóstico Aprofundado',
    description: 'Avaliação completa da situação atual com relatório detalhado e recomendações',
    badge: 'Recomendado',
  },
  {
    id: 'specific_projects',
    label: 'Projetos Específicos',
    description: 'Apoio dedicado a projetos concretos como candidaturas, auditorias ou reorganizações',
    badge: null,
  },
];

const URGENCY_INDICATORS = [
  { id: 'immediate', label: 'Imediata — Situação urgente' },
  { id: 'short_term', label: 'Curto prazo — Nos próximos 3 meses' },
  { id: 'medium_term', label: 'Médio prazo — Nos próximos 6 meses' },
  { id: 'exploring', label: 'A avaliar — Apenas a explorar opções' },
];

export default function StepSupportPreferences({ data, onChange }: StepSupportPreferencesProps) {
  const handlePreference = (id: string) => {
    onChange({ ...data, preference: id });
  };

  const handleNotes = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...data, additionalNotes: e.target.value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: '5px', backgroundColor: '#E0F2F1', color: '#009688' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M18 11V6l-6-3-6 3v5a9 9 0 006 8.485A9 9 0 0018 11z"/>
            </svg>
          </div>
          <div>
            <h2
              className="text-gray-900 mb-1"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}
            >
              Preferências de Apoio
            </h2>
            <p
              className="text-gray-500 text-sm leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Indique o tipo de apoio que melhor se adequa às necessidades da sua Junta de Freguesia.
            </p>
          </div>
        </div>
      </div>

      {/* Support Type Selection */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-1 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
          </svg>
          Tipo de Apoio Preferido
        </h3>
        <p className="text-gray-400 text-xs mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Qual o modelo de apoio que melhor se adequa à sua situação? <span className="text-error">*</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SUPPORT_OPTIONS.map((opt) => {
            const selected = data.preference === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handlePreference(opt.id)}
                className="relative flex items-start gap-4 p-5 text-left transition-all duration-200 min-h-[44px]"
                style={{
                  borderRadius: '5px',
                  border: selected ? '2px solid #009688' : '2px solid #F0F0F0',
                  backgroundColor: selected ? '#F0FAFA' : 'white',
                }}
                aria-pressed={selected}
              >
                {opt.badge && (
                  <span
                    className="absolute top-3 right-3 px-2 py-0.5 text-xs font-semibold"
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      borderRadius: '5px',
                      backgroundColor: selected ? '#009688' : '#F5F5F5',
                      color: selected ? '#fff' : '#666',
                    }}
                  >
                    {opt.badge}
                  </span>
                )}
                <div
                  className="w-9 h-9 flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                  style={{
                    borderRadius: '5px',
                    backgroundColor: selected ? '#009688' : '#F5F5F5',
                    color: selected ? 'white' : '#666',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0 pr-8">
                  <p
                    className="font-semibold text-sm mb-1"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: selected ? '#009688' : '#1A1A1A' }}
                  >
                    {opt.label}
                  </p>
                  <p
                    className="text-xs leading-relaxed"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: '#888' }}
                  >
                    {opt.description}
                  </p>
                </div>
                <div
                  className="absolute bottom-4 right-4 w-4 h-4 border-2 flex items-center justify-center transition-all duration-200"
                  style={{
                    borderRadius: '50%',
                    backgroundColor: selected ? '#009688' : 'transparent',
                    borderColor: selected ? '#009688' : '#D0D0D0',
                  }}
                >
                  {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Urgency */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-1 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Urgência da Necessidade
        </h3>
        <p className="text-gray-400 text-xs mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Quando prevê necessitar de apoio? (opcional)
        </p>
        <div className="space-y-2">
          {URGENCY_INDICATORS.map((urg) => {
            const selected = data.additionalNotes.startsWith(`urgency:${urg.id}`);
            return (
              <button
                key={urg.id}
                type="button"
                onClick={() => {
                  const notes = data.additionalNotes.replace(/^urgency:\w+\n?/, '');
                  onChange({ ...data, additionalNotes: `urgency:${urg.id}\n${notes}` });
                }}
                className="w-full flex items-center gap-3 p-3.5 text-left transition-all duration-200 min-h-[44px]"
                style={{
                  borderRadius: '5px',
                  border: selected ? '2px solid #009688' : '2px solid #F0F0F0',
                  backgroundColor: selected ? '#F0FAFA' : 'white',
                }}
                aria-pressed={selected}
              >
                <div
                  className="w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{
                    borderRadius: '50%',
                    backgroundColor: selected ? '#009688' : 'transparent',
                    borderColor: selected ? '#009688' : '#D0D0D0',
                  }}
                >
                  {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <span
                  className="text-sm"
                  style={{ fontFamily: 'DM Sans, sans-serif', color: selected ? '#009688' : '#1A1A1A', fontWeight: selected ? 600 : 400 }}
                >
                  {urg.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Additional Notes */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-1 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          Observações Adicionais
        </h3>
        <p className="text-gray-400 text-xs mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Partilhe qualquer informação adicional que considere relevante para o diagnóstico (opcional)
        </p>
        <textarea
          className="w-full px-4 py-3 border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 placeholder:text-gray-400 resize-none"
          style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px' }}
          rows={4}
          placeholder="Ex: Temos uma situação específica com contratos de pessoal que gostaríamos de resolver..."
          value={data.additionalNotes.replace(/^urgency:\w+\n?/, '')}
          onChange={handleNotes}
          maxLength={500}
        />
        <div className="flex justify-end mt-1">
          <span className="text-gray-400 text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {data.additionalNotes.replace(/^urgency:\w+\n?/, '').length}/500
          </span>
        </div>
      </div>

      {/* Completion note */}
      <div
        className="flex items-start gap-3 px-5 py-4"
        style={{ borderRadius: '5px', border: '1px solid #E0F2F1', backgroundColor: '#F0FAFA' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" className="flex-shrink-0 mt-0.5" aria-hidden="true">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <div>
          <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'DM Sans, sans-serif', color: '#009688' }}>
            Está quase a concluir o diagnóstico
          </p>
          <p className="text-gray-500 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Após submeter, receberá um diagnóstico personalizado com o perfil da sua Junta de Freguesia e recomendações específicas para as suas necessidades.
          </p>
        </div>
      </div>
    </div>
  );
}